import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { heroContent } from '../mockData';
import { Button } from './ui/button';
import { ArrowRight, Check } from 'lucide-react';
import {
  blurFade,
  createFloatingAnimation,
  createStaggerContainer,
  fadeInRight,
  fadeInUp,
  getHeroAnimationProps,
  scaleIn,
} from '../lib/animations';

const HeroSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const heroAnimationProps = getHeroAnimationProps(shouldReduceMotion);
  const containerVariants = shouldReduceMotion
    ? undefined
    : createStaggerContainer({ staggerChildren: 0.25, delayChildren: 0.2 });
  const textStagger = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18 });

  return (
    <section id="accueil" className="relative bg-[#F5F0E8] overflow-hidden">
      {!shouldReduceMotion && (
        <>
          <motion.div
            className="absolute -top-24 -right-16 w-64 h-64 rounded-full bg-[#C9B9A3]/30 blur-3xl"
            animate={createFloatingAnimation({ offset: 26, duration: 26 })}
          />
          <motion.div
            className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-[#7D8471]/25 blur-3xl"
            animate={createFloatingAnimation({ offset: 22, duration: 28, delay: 4 })}
          />
        </>
      )}
      <motion.div className="max-w-7xl mx-auto" {...heroAnimationProps} variants={containerVariants}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[500px] sm:min-h-[600px]">
          <motion.div className="px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-20 order-2 lg:order-1" variants={textStagger}>
            <div className="relative">
              <motion.div className="relative z-10" variants={textStagger}>
                <motion.div className="inline-block mb-4 sm:mb-6" variants={scaleIn}>
                  <span className="text-xs sm:text-sm uppercase tracking-widest text-[#7D8471] font-medium bg-[#7D8471]/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    {heroContent.badge}
                  </span>
                </motion.div>

                <motion.h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5A5045] mb-4 sm:mb-6 leading-tight" variants={fadeInUp}>
                  {heroContent.title}
                </motion.h1>

                <motion.p className="text-sm sm:text-base text-[#6B5E54] mb-6 sm:mb-8 leading-relaxed" variants={fadeInUp}>
                  {heroContent.description}
                </motion.p>

                <motion.div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8" variants={textStagger}>
                  {heroContent.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="bg-white/60 p-3 sm:p-4 rounded-sm border-l-4 border-[#7D8471]"
                      variants={blurFade}
                      whileHover={shouldReduceMotion ? undefined : { translateY: -4, boxShadow: '0 18px 40px rgba(125, 132, 113, 0.18)' }}
                    >
                      <div className="flex items-start space-x-3">
                        <Check className="text-[#7D8471] flex-shrink-0 mt-0.5" size={18} />
                        <div>
                          <h3 className="text-sm sm:text-base font-semibold text-[#5A5045] mb-1">{feature.title}</h3>
                          <p className="text-xs sm:text-sm text-[#6B5E54]">{feature.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="flex flex-col sm:flex-row gap-3 sm:gap-4" variants={textStagger}>
                  <motion.a
                    href="#contact"
                    className="flex-1 sm:flex-initial"
                    whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                  >
                    <Button className="w-full sm:w-auto bg-[#7D8471] hover:bg-[#6B7361] text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-sm transition-all duration-300 hover:shadow-2xl group">
                      {heroContent.ctaButton1}
                      <ArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-1" size={18} />
                    </Button>
                  </motion.a>
                  <motion.a
                    href="#methode"
                    className="flex-1 sm:flex-initial"
                    whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                  >
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto border-[#7D8471] text-[#7D8471] hover:bg-[#7D8471] hover:text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-sm transition-all duration-300"
                    >
                      {heroContent.ctaButton2}
                    </Button>
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div className="relative order-1 lg:order-2 h-[300px] sm:h-[400px] lg:h-[600px]" variants={fadeInRight}>
            <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-l from-transparent to-[#F5F0E8]/50 z-10"></div>
            <motion.img
              src={heroContent.image}
              alt="Espace de thérapie calme et serein"
              className="w-full h-full object-cover"
              {...(shouldReduceMotion
                ? {}
                : {
                    initial: { scale: 1.05 },
                    animate: { scale: 1 },
                    transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
                  })}
            />
            {!shouldReduceMotion && (
              <motion.div
                className="absolute bottom-6 right-6 bg-white/70 backdrop-blur-sm px-4 py-3 rounded-sm border border-white/40 text-[#5A5045] shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="text-xs sm:text-sm font-medium">Accompagnement neuro-thérapeutique pour rééquilibrer durablement vos systèmes</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;